import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { MotionConfig } from "framer-motion";
import { AuthProvider } from "@/lib/auth";
import { ActivityProvider } from "@/lib/activity";
import MobileNav from "@/components/MobileNav";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { initNative, applyStatusBar } from "@/lib/native";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import SearchOverlay from "@/components/SearchOverlay";
import LocationGate from "@/components/LocationGate";
import HomePage from "@/pages/HomePage";
import BrowsePage from "@/pages/BrowsePage";
import NegotiationRoomPage from "@/pages/NegotiationRoomPage";
import AdminProductRequestsPage from "@/pages/AdminProductRequestsPage";
import AdminHireRequestsPage from "@/pages/AdminHireRequestsPage";
import DealDetailPage from "@/pages/DealDetailPage";
import AdminPage from "@/pages/AdminPage";
import AdminBhegaCsvPage from "@/pages/AdminBhegaCsvPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import AboutUsPage from "@/pages/AboutUsPage";
import MePage from "@/pages/MePage";
import api, { productUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Hard-coded category slugs — these take precedence at the top level over product slugs.
const CATEGORY_SLUGS = new Set(["automobile", "automobiles", "electronics", "real_estate", "real-estate"]);
// Reserved top-level paths the SPA must keep — slug resolution skips these.
const RESERVED_SLUGS = new Set([
  "", "browse", "admin", "me", "about", "about-us", "how-it-works",
  "p", "n", "d", "c", "cat", "api", "search", "login", "signup", "static",
]);

function Shell() {
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setSearchOpen(true);
      }
    };
    const openAuth = () => setAuthOpen(true);
    window.addEventListener("keydown", handler);
    window.addEventListener("bhega:open-auth", openAuth);
    return () => { window.removeEventListener("keydown", handler); window.removeEventListener("bhega:open-auth", openAuth); };
  }, []);

  // Native (iOS/Android) boot — no-op on web
  useEffect(() => { initNative(navigate); }, [navigate]);
  useEffect(() => { applyStatusBar(theme); }, [theme]);

  return (
    <>
      <Header onOpenAuth={() => setAuthOpen(true)} onOpenSearch={() => setSearchOpen(true)} />
      <main className="min-h-[60vh]" id="main">
        <Routes>
          <Route path="/" element={<HomePage onOpenAuth={() => setAuthOpen(true)} />} />
          <Route path="/browse" element={<BrowsePage />} />
          {/* Legacy /c/<cat> redirects to /<cat> */}
          <Route path="/c/:category" element={<CategoryRedirect />} />
          {/* Legacy /p/<id> + /p/<id>/legacy redirect to /<slug> */}
          <Route path="/p/:id" element={<RedirectByProductId />} />
          <Route path="/p/:id/legacy" element={<RedirectByProductId />} />
          {/* Legacy /n/<productId> redirects to /<slug> */}
          <Route path="/n/:productId" element={<RedirectByProductId />} />
          <Route path="/d/:id" element={<DealDetailPage onOpenAuth={() => setAuthOpen(true)} />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/hire-requests" element={<AdminHireRequestsPage />} />
          <Route path="/admin/product-requests" element={<AdminProductRequestsPage />} />
          <Route path="/admin/bhega-csv" element={<AdminBhegaCsvPage />} />
          <Route path="/admin/reports" element={<AdminReportsPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/me" element={<MePage />} />
          {/* Catch-all top-level slug: either a category page or a negotiation room */}
          <Route path="/:slug" element={<SlugRouter onOpenAuth={() => setAuthOpen(true)} />} />
        </Routes>
      </main>
      <div className="has-bottom-nav"><Footer /></div>
      <MobileNav onOpenSearch={() => setSearchOpen(true)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <LocationGate />
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <ActivityProvider>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
          </ActivityProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
    </MotionConfig>
  );
}

function CategoryRedirect() {
  const { category } = useParams();
  return <Navigate to={`/${category}`} replace />;
}

function RedirectByProductId() {
  const params = useParams();
  const id = params.id || params.productId;
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    api.get(`/products/${id}`).then((r) => {
      if (!active) return;
      navigate(`/${r.data.slug || ""}`, { replace: true });
    }).catch(() => { if (active) navigate("/browse", { replace: true }); });
    return () => { active = false; };
  }, [id, navigate]);
  return <div className="max-w-4xl mx-auto px-4 py-20 text-[#737373]">Loading…</div>;
}

/**
 * /<slug> — decide whether the slug is a category or a product.
 * - Categories (automobile / electronics / real_estate) render BrowsePage filtered by category.
 * - Anything else resolves through /api/products/by-slug/<slug> and renders the Negotiation Room.
 */
function SlugRouter({ onOpenAuth }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { city } = useAuth();
  const isCategory = !!slug && CATEGORY_SLUGS.has(slug);
  const isReserved = !!slug && RESERVED_SLUGS.has(slug);
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (isCategory || isReserved) return;
    let active = true;
    setState({ loading: true });
    // Pass `city` so the resolved product carries the scope-aware bhega_count
    // — keeps the room badge in lock-step with the BrowsePage card.
    api.get(`/products/by-slug/${slug}`, { params: city ? { city } : {} }).then((r) => {
      if (!active) return;
      setState({ loading: false, product: r.data });
    }).catch(() => {
      if (!active) return;
      setState({ loading: false, product: null });
    });
    return () => { active = false; };
  }, [slug, isCategory, isReserved, city]);

  if (isCategory) {
    const cat = slug === "real-estate" ? "real_estate" : slug;
    return <BrowsePage category={cat} />;
  }
  if (isReserved) return <Navigate to="/browse" replace />;
  if (state.loading) return <div className="max-w-4xl mx-auto px-4 py-20 text-[#737373]">Loading…</div>;
  if (!state.product) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-heading font-extrabold">We couldn&apos;t find that product</h1>
        <p className="text-sm text-[#525252] mt-2">It may have been delisted, or the URL is mistyped.</p>
        <button onClick={() => navigate("/browse")} className="btn-primary mt-6">Back to catalog</button>
      </div>
    );
  }
  return <NegotiationRoomPage onOpenAuth={onOpenAuth} productOverride={state.product} />;
}

export default App;
